/**
 * HomePage - Landing Page
 * ThePedigreeApp
 * 
 * Features: 30-day free trial, How It Works, R1,500 per litter pricing
 */

import { Link } from 'react-router-dom';
import { 
  Cat, Shield, Award, Users, ArrowRight, CheckCircle, Star,
  Search, Heart, FileText, Sparkles, Clock, Baby, DollarSign,
  ChevronRight, Play, Zap
} from 'lucide-react';
import { Button, Badge, Card } from '../components/shared/UIComponents';

const HomePage = () => {
  return (
    <div className="min-h-screen">
      
      {/* HERO SECTION */}
      <section className="relative bg-gradient-to-br from-pedigree-600 via-pedigree-700 to-pedigree-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-white/20 text-white mb-6">
                <Sparkles className="w-4 h-4 mr-1" /> 30-Day Free Trial
              </Badge>
              
              <h1 className="text-4xl lg:text-6xl font-display font-bold mb-6 leading-tight">
                South Africa's Trusted
                <span className="block text-pedigree-200">Pedigree Cat Platform</span>
              </h1>
              
              <p className="text-xl text-pedigree-100 mb-8 max-w-lg">
                The complete cattery management system for professional breeders. 
                Register cats, track litters, manage sales, and connect with buyers.
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button size="lg" className="bg-white text-pedigree-600 hover:bg-pedigree-50">
                    Start Free Trial <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/marketplace">
                  <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                    Browse Kittens
                  </Button>
                </Link>
              </div>
              
              <div className="mt-8 flex items-center gap-6 text-sm text-pedigree-200">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  No credit card required
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  Full access for 30 days
                </div>
              </div>
            </div>
            
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-t from-pedigree-700/50 to-transparent rounded-3xl" />
              <img 
                src="/hero-cat.jpg" 
                alt="Pedigree Cat" 
                className="rounded-3xl shadow-2xl"
                onError={(e) => {
                  e.target.src = 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=600&h=700&fit=crop';
                }}
              />
              
              <div className="absolute -left-8 top-1/4 bg-white rounded-2xl p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-pedigree-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-pedigree-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">500+</p>
                    <p className="text-sm text-gray-500">Verified Breeders</p>
                  </div>
                </div>
              </div>
              
              <div className="absolute -right-8 bottom-1/4 bg-white rounded-2xl p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <Cat className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">2,500+</p>
                    <p className="text-sm text-gray-500">Cats Registered</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUSTED BY */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 text-gray-400">
            <p className="text-sm font-medium">Trusted by breeders registered with:</p>
            <div className="flex items-center gap-8">
              <span className="font-bold text-gray-600">SACC</span>
              <span className="font-bold text-gray-600">CASA</span>
              <span className="font-bold text-gray-600">CFSA</span>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-pedigree-100 text-pedigree-700 mb-4">How It Works</Badge>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-gray-900 mb-4">
              Get Started in Minutes
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto">
              Join South Africa's fastest-growing pedigree cat community
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-pedigree-500 text-white rounded-full flex items-center justify-center font-bold text-xl">
                1
              </div>
              <Card className="pt-12 p-6 h-full">
                <div className="w-16 h-16 bg-pedigree-100 rounded-2xl flex items-center justify-center mb-4">
                  <FileText className="w-8 h-8 text-pedigree-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Register & Verify</h3>
                <p className="text-gray-500">
                  Sign up with your registry details (SACC, CASA, or CFSA). 
                  We verify your credentials to ensure a trusted community.
                </p>
                <div className="mt-4 p-3 bg-green-50 rounded-lg">
                  <p className="text-sm text-green-700 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <strong>30-day free trial</strong> - No payment required
                  </p>
                </div>
              </Card>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-pedigree-500 text-white rounded-full flex items-center justify-center font-bold text-xl">
                2
              </div>
              <Card className="pt-12 p-6 h-full">
                <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center mb-4">
                  <Cat className="w-8 h-8 text-pink-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Manage Your Cattery</h3>
                <p className="text-gray-500">
                  Add your cats, track pedigrees, plan breedings, record health data, 
                  and manage your entire cattery from one dashboard.
                </p>
                <ul className="mt-4 space-y-2 text-sm text-gray-600">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> 5-gen pedigree tracking</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Genetics calculator</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /> Health records</li>
                </ul>
              </Card>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 w-12 h-12 bg-pedigree-500 text-white rounded-full flex items-center justify-center font-bold text-xl">
                3
              </div>
              <Card className="pt-12 p-6 h-full">
                <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mb-4">
                  <Baby className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Register & Sell Litters</h3>
                <p className="text-gray-500">
                  Register your litters, list kittens for sale, and connect with 
                  verified buyers across South Africa.
                </p>
                <div className="mt-4 p-3 bg-pedigree-50 rounded-lg">
                  <p className="text-sm text-pedigree-700">
                    <strong>Simple pricing:</strong> R1,500 per litter registered
                  </p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-pedigree-100 text-pedigree-700 mb-4">Simple Pricing</Badge>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-gray-900 mb-4">
              One Simple Fee, No Surprises
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto">
              Start free, pay only when you register litters
            </p>
          </div>

          <div className="max-w-lg mx-auto">
            <Card className="overflow-hidden">
              <div className="bg-gradient-to-r from-pedigree-500 to-pedigree-600 text-white p-6 text-center">
                <Badge className="bg-white/20 text-white mb-3">Most Popular</Badge>
                <h3 className="text-2xl font-bold mb-2">Professional Breeder</h3>
                <p className="text-pedigree-100">Everything you need to run your cattery</p>
              </div>
              
              <div className="p-8">
                <div className="text-center mb-8 pb-8 border-b">
                  <p className="text-sm text-gray-500 uppercase tracking-wide mb-2">Start with</p>
                  <p className="text-5xl font-bold text-gray-900">30 Days</p>
                  <p className="text-xl text-pedigree-600 font-medium">FREE Trial</p>
                  <p className="text-gray-500 mt-2">Full access, no credit card required</p>
                </div>
                
                <div className="text-center mb-8">
                  <p className="text-sm text-gray-500 uppercase tracking-wide mb-2">Then only</p>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-gray-900">R1,500</span>
                    <span className="text-gray-500">/ litter</span>
                  </div>
                  <p className="text-gray-500 mt-2">Charged when you register a litter with SACC/CASA/CFSA</p>
                </div>
                
                <div className="space-y-3 mb-8">
                  <p className="font-medium text-gray-900">Everything included:</p>
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      'Unlimited cat profiles',
                      '5-generation pedigree tracking',
                      'Genetics & COI calculator',
                      'Health record management',
                      'Customer & sales tracking',
                      'Vendor management',
                      'Financial reporting',
                      'Marketplace listings',
                      'Verified breeder badge',
                      'Priority support',
                    ].map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-gray-600">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
                
                <Link to="/register">
                  <Button className="w-full" size="lg">
                    Start Your Free Trial <ArrowRight className="w-5 h-5" />
                  </Button>
                </Link>
                
                <p className="text-center text-sm text-gray-400 mt-4">
                  No commitment. Cancel anytime.
                </p>
              </div>
            </Card>
          </div>
          
          <div className="mt-12 max-w-2xl mx-auto text-center">
            <h4 className="font-semibold text-gray-900 mb-2">Why R1,500 per litter?</h4>
            <p className="text-gray-500">
              Our fee helps keep the platform running smoothly, ensures all breeders are verified, 
              and supports ongoing development of new features. You only pay when you have 
              kittens to sell - no monthly fees eating into your profits.
            </p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="bg-pedigree-100 text-pedigree-700 mb-4">Features</Badge>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-gray-900 mb-4">
              Everything You Need to Run Your Cattery
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Cat, title: 'Cat Management', desc: 'Complete profiles with photos, pedigrees, and registration details' },
              { icon: Heart, title: 'Breeding Program', desc: 'Plan matings, predict genetics, track breeding cycles' },
              { icon: Baby, title: 'Litter Tracking', desc: 'Register litters, monitor kitten development, manage sales' },
              { icon: Shield, title: 'Health Records', desc: 'Vaccinations, vet visits, genetic testing, and more' },
              { icon: DollarSign, title: 'Financial Tools', desc: 'Track income, expenses, and profitability per cat' },
              { icon: Users, title: 'Customer Management', desc: 'Handle enquiries, contracts, and sales records' },
            ].map((feature, i) => (
              <Card key={i} className="p-6 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 bg-pedigree-100 rounded-xl flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-pedigree-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-500">{feature.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-pedigree-600 to-pedigree-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-display font-bold mb-6">
            Ready to Grow Your Cattery?
          </h2>
          <p className="text-xl text-pedigree-100 mb-8">
            Join 500+ South African breeders already using ThePedigreeApp
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-white text-pedigree-600 hover:bg-pedigree-50">
                Start Your 30-Day Free Trial <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
